import { Input } from "@/components/ui/input";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToastError } from "@/components/ui/use-toast";
import { useListings } from "@/contexts/listings-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import { useArePeopleVisible } from "@/hooks/useArePeopleVisible";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { AssignationsDTO } from "@shaarp/common";
import { Search, X } from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router-dom";
import AssignedCategoriesList from "./listing/assigned-categories-list";
import CollaboratorsListingsList from "./listing/collaborators-listings-list";
import ListingsList from "./listing/listings-list";

type ListingTab = "personal" | "assigned" | "team" | "shaarp";

export default function ListingPage() {
  const api = useApi();
  const location = useLocation();
  const { t } = useTranslation();
  const toastError = useToastError();

  const { listingId } = useParams();

  const {
    userListings,
    shaarpListings,
    assignedListings,
    collaboratorsListings,
    apiFetchListings,
    apiFetchAssignedListings,
    apiFetchCollaboratorsListings,
  } = useListings();
  const { canViewCollaborators, teamSelected } = useTeams();
  // A team whose contact switch is off sees no key contacts (#757): its assigned
  // contacts do not count.
  const arePeopleVisible = useArePeopleVisible();

  const [searchQuery, setSearchQuery] = useState<string>("");
  const [activeTab, setActiveTab] = useState<ListingTab>("personal");

  const [assignations, setAssignations] = useState<AssignationsDTO>({
    companiesCount: 0,
    employeesCount: 0,
    listingsCount: 0,
    lastUpdatedAt: null,
  });

  useEffect(() => {
    const init = async () => {
      await apiFetchListings({ force: true });
      await apiFetchAssignedListings({ force: true });
      if (canViewCollaborators) {
        await apiFetchCollaboratorsListings({ force: true });
      }
    };
    init();
  }, [canViewCollaborators, teamSelected?.id]);

  const fetchAssignations = async () => {
    try {
      const { data } = await api.get<AssignationsDTO>(`/listing/assignations`);
      setAssignations(data);
    } catch (error) {
      toastError();
      setAssignations({
        companiesCount: 0,
        employeesCount: 0,
        listingsCount: 0,
        lastUpdatedAt: null,
      });
    }
  };

  useEffect(() => {
    fetchAssignations();
  }, [location.pathname]);

  const calculateRelevanceScore = (
    listing: (typeof userListings)[0],
    query: string
  ) => {
    const title = listing.title?.toLowerCase() || "";
    const description = listing.description?.toLowerCase() || "";
    const authorName = listing.author?.fullName?.toLowerCase() || "";

    let score = 0;
    if (title.includes(query)) {
      score += title.startsWith(query) ? 100 : 50;
    }
    if (description.includes(query)) {
      score += 20;
    }
    if (authorName.includes(query)) {
      score += 10;
    }
    return score;
  };

  const filterAndSortAllListings = () => {
    if (!searchQuery) {
      return [];
    }

    const query = searchQuery.toLowerCase();
    const allListings = [
      ...userListings,
      ...assignedListings,
      ...shaarpListings,
    ];

    return allListings
      .map((listing) => ({
        listing,
        score: calculateRelevanceScore(listing, query),
      }))
      .filter((item) => item.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((item) => item.listing);
  };

  const assignedTotal =
    assignations.companiesCount +
    (arePeopleVisible ? assignations.employeesCount : 0) +
    assignations.listingsCount;

  const collaboratorsTotal = collaboratorsListings.reduce(
    (acc, c) => acc + c.listings.length,
    0
  );

  const searchPlaceholder =
    activeTab === "assigned"
      ? t("listing.page.searchPlaceholderAssigned")
      : t("listing.page.searchPlaceholder");

  const isAdminSupportMode = teamSelected?.external === true;
  const personalTabLabel = isAdminSupportMode
    ? t("listing.page.tabs.personalAdminSupport")
    : t("listing.page.tabs.personal");

  const tabTriggerClass =
    "group flex flex-col justify-center items-center gap-0.5 w-full py-2 h-auto data-[state=active]:bg-background data-[state=active]:shadow-sm";
  const tabLabelClass =
    "text-xs font-medium text-muted-foreground group-data-[state=active]:text-foreground";
  const tabCountClass =
    "text-sm font-bold text-muted-foreground group-data-[state=active]:text-majorelle";

  return (
    <>
      <div className="absolute top-(--app-header-height) left-0 flex-1 w-full h-[calc(100%-var(--app-header-height))] overflow-auto">
        <ResizablePanelGroup orientation="horizontal" className="w-full">
          <ResizablePanel
            defaultSize="22%"
            minSize="380px"
            maxSize="420px"
            className="relative bg-background"
          >
            <div className="overflow-auto absolute top-0 left-0 flex-1 p-2 pt-6 space-y-5 w-full h-full">
              <div className="space-y-2">
                <div className="flex-1 p-1 pt-0 space-y-5">
                  <div className="flex justify-between">
                    <h2 className="text-2xl font-bold tracking-tight">
                      {t("listing.page.title")}
                    </h2>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 w-4 h-4 -translate-y-1/2 pointer-events-none text-muted-foreground" />
                    <Input
                      placeholder={searchPlaceholder}
                      className="pr-8 pl-9 w-full bg-background"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery("")}
                        className="absolute right-2 top-1/2 transition-colors -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                  {searchQuery ? (
                    <div className="space-y-2">
                      {filterAndSortAllListings().length === 0 ? (
                        <div className="flex flex-col justify-center items-center py-8 w-full">
                          <p className="text-sm text-center text-muted-foreground">
                            {t("listing.page.noResults")}
                          </p>
                        </div>
                      ) : (
                        <ListingsList
                          listings={filterAndSortAllListings()}
                          listingId={listingId}
                        />
                      )}
                    </div>
                  ) : (
                    <Tabs
                      value={activeTab}
                      onValueChange={(value) =>
                        setActiveTab(value as ListingTab)
                      }
                    >
                      <TabsList className="flex gap-1 p-1 w-full h-auto">
                        <TabsTrigger
                          value="personal"
                          className={cn(tabTriggerClass)}
                        >
                          <span className={tabLabelClass}>
                            {personalTabLabel}
                          </span>
                          <span className={tabCountClass}>
                            {userListings.length}
                          </span>
                        </TabsTrigger>
                        <TabsTrigger
                          value="assigned"
                          className={cn(tabTriggerClass)}
                        >
                          <span className={tabLabelClass}>
                            {t("listing.page.tabs.assigned")}
                          </span>
                          <span className={tabCountClass}>
                            {assignedTotal}
                          </span>
                        </TabsTrigger>
                        {canViewCollaborators && (
                          <TabsTrigger
                            value="team"
                            className={cn(tabTriggerClass)}
                          >
                            <span className={tabLabelClass}>
                              {t("listing.page.tabs.team")}
                            </span>
                            <span className={tabCountClass}>
                              {collaboratorsTotal}
                            </span>
                          </TabsTrigger>
                        )}
                        {shaarpListings.length > 0 && (
                          <TabsTrigger
                            value="shaarp"
                            className={cn(tabTriggerClass)}
                          >
                            <span className={tabLabelClass}>
                              {t("listing.page.tabs.shaarp")}
                            </span>
                            <span className={tabCountClass}>
                              {shaarpListings.length}
                            </span>
                          </TabsTrigger>
                        )}
                      </TabsList>
                      <TabsContent value="personal" className="space-y-2">
                        <ListingsList
                          listings={userListings}
                          listingId={listingId}
                          defaultOwnerType="user"
                        />
                      </TabsContent>
                      <TabsContent value="assigned" className="space-y-2">
                        <AssignedCategoriesList
                          assignations={assignations}
                          assignedListings={assignedListings}
                          listingId={listingId}
                        />
                      </TabsContent>
                      {canViewCollaborators && (
                        <TabsContent value="team">
                          <CollaboratorsListingsList
                            collaborators={collaboratorsListings}
                            listingId={listingId}
                          />
                        </TabsContent>
                      )}
                      <TabsContent value="shaarp">
                        <ListingsList
                          listings={shaarpListings}
                          listingId={listingId}
                          defaultOwnerType="shaarp"
                        />
                      </TabsContent>
                    </Tabs>
                  )}
                </div>
              </div>
            </div>
          </ResizablePanel>
          <ResizableHandle disabled={true} />
          <ResizablePanel className="relative">
            <Outlet />
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </>
  );
}
